package com.arena_web.ui.sections.news

import androidx.compose.runtime.*
import com.arena_web.data.dto.NewsArticleDto
import com.arena_web.ui.components.navigation.NavAnchor
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// =============================================================================
// Arena News — feed mode (design spec:
// docs/superpowers/specs/2026-09-14-arena-news-design.md).
// "Today's news" horizontal carousel + "All news" list with sort segments.
// =============================================================================

enum class NewsFeedSort { NEW, POPULAR, FAVORITES }

private const val MOBILE_SCROLL_ID = "news-feed-scroll-mobile"
private const val DESKTOP_SCROLL_ID = "news-feed-scroll-desktop"
private const val LOAD_MORE_THRESHOLD_PX = 300

/** Feed scroll offset carried across SPA navigation (article page and back).
 *  In-memory only — a full page load starts at the top as usual. The scroll
 *  listener keeps [lastScrollTop] current; NewsPage moves it into
 *  [pendingRestore] when it reopens the feed from its session cache. */
internal object NewsFeedScrollMemory {
    var lastScrollTop: Double = 0.0
    var pendingRestore: Double = 0.0
}

/** Near-bottom scroll trigger on the container with [id] — same pattern as
 *  the Feed page's infinite scroll. [contentKey] should change whenever the
 *  list re-renders (e.g. its size). */
@Composable
private fun InfiniteScrollTrigger(id: String, contentKey: Any?, onLoadMore: () -> Unit) {
    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(id)
        val handler: (org.w3c.dom.events.Event) -> Unit = {
            val node = kotlinx.browser.document.getElementById(id)
            if (node != null) {
                NewsFeedScrollMemory.lastScrollTop = node.scrollTop
                if (node.scrollHeight - node.scrollTop - node.clientHeight < LOAD_MORE_THRESHOLD_PX) {
                    onLoadMore()
                }
            }
        }
        el?.addEventListener("scroll", handler)
        onDispose { el?.removeEventListener("scroll", handler) }
    }
    // After each render: first apply a pending cross-navigation scroll restore
    // (once the content is tall enough to hold it), then top up a page that
    // doesn't overflow the container — such a page emits no scroll events at
    // all (onLoadMore's hasMore/loading guards stop the chain).
    LaunchedEffect(contentKey) {
        kotlinx.browser.window.requestAnimationFrame {
            val el = kotlinx.browser.document.getElementById(id) ?: return@requestAnimationFrame
            val pending = NewsFeedScrollMemory.pendingRestore
            if (pending > 0 && el.scrollHeight - el.clientHeight >= pending) {
                el.scrollTop = pending
                NewsFeedScrollMemory.pendingRestore = 0.0
            }
            if (el.scrollHeight <= el.clientHeight) onLoadMore()
        }
    }
}

// =============================================================================
// Mobile
// =============================================================================

@Composable
fun NewsContentMobile(
    modeToggle: @Composable () -> Unit = {},
    todayArticles: List<NewsArticleDto>,
    feedArticles: List<NewsArticleDto>,
    sort: NewsFeedSort,
    onSortChange: (NewsFeedSort) -> Unit,
    activeTag: String? = null,
    onClearTag: () -> Unit = {},
    onLoadMore: () -> Unit = {},
    showProBanner: Boolean = false,
    onProBannerClick: () -> Unit = {},
) {
    val t = LocalAppStrings.current
    // Non-subscribers get a taste of the feed, then the PRO gate: the list is
    // cut after the free cards and the banner closes it — no point fetching
    // pages nobody will see, so infinite scroll is off while gated.
    InfiniteScrollTrigger(MOBILE_SCROLL_ID, feedArticles.size, if (showProBanner) ({}) else onLoadMore)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
    ) {
        // Single scroll container — carousel + list scroll together, same
        // pattern as the Feed page.
        Div(
            attrs = {
                id(MOBILE_SCROLL_ID)
                style {
                    // Full-bleed scroller: break out of the layout's 16-px
                    // gutter so the overflow-x clip lands on the TRUE screen
                    // edge — otherwise the Today carousel's own breakout gets
                    // cut at the gutter and side insets show while scrolling.
                    // The inner column re-adds the gutter for regular content.
                    property("width", "calc(100% + 32px)")
                    property("margin-left", "-16px")
                    property("margin-right", "-16px")
                    property("flex-grow", "1")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                    property("scrollbar-width", "none")
                }
            }
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("gap", "14px")
                    property("padding-left", "16px")
                    property("padding-right", "16px")
                    property("box-sizing", "border-box")
                }
            ) {
                // Top row: "Today's news" opposite the news↔matches switcher
                // (the switcher lives here now, not in the top bar). A tag
                // filter narrows the whole surface — the unfiltered "today"
                // strip would just confuse next to it, so the title yields.
                val showToday = todayArticles.isNotEmpty() && activeTag == null
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (showToday) SectionHeader(t.newsTrendingTitle, mobile = true) else Box {}
                    modeToggle()
                }
                if (showToday) {
                    TodayCarouselMobile(todayArticles)
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (activeTag != null) ActiveTagPill(activeTag, onClearTag, mobile = true)
                    else SectionHeader(t.newsAllTitle, mobile = true)
                    SortSegments(sort, onSortChange, mobile = true)
                }

                if (feedArticles.isEmpty()) {
                    Box(
                        modifier = Modifier.fillMaxWidth().minHeight(160.px).padding(top = 24.px),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            t.newsEmptyDay,
                            modifier = Modifier.mFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text2)
                        )
                    }
                } else {
                    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "12px") }) {
                        // Gated feed: 3 free cards, then the PRO banner ends
                        // the list — the rest of the feed is subscribers-only.
                        val visible = if (showProBanner) feedArticles.take(3) else feedArticles
                        visible.forEach { article -> ArticleLink(article) { NewsCardMobile(article) } }
                        if (showProBanner) {
                            NewsProBanner(mobile = true, onClick = onProBannerClick)
                        }
                    }
                }

                // Bottom spacer — lifts the last card above the fixed
                // BottomNavBar (padding-bottom on the scroller is unreliable
                // at scroll-end, so emit a real sized Div).
                Div(
                    attrs = {
                        style {
                            property("width", "100%")
                            property("flex", "0 0 120px")
                            property("height", "120px")
                        }
                    }
                )
            }
        }
    }
}

/** Horizontal snap carousel of today's articles — cover-filled cards with the
 *  title over a bottom gradient. */
@Composable
private fun TodayCarouselMobile(articles: List<NewsArticleDto>) {
    Row(
        modifier = Modifier
            .styleModifier {
                // Break out of the layout's 16-px gutter so the strip scrolls
                // edge-to-edge; BOTH gutters come back as real spacer Divs
                // inside the scroller (not padding: end-padding is dropped at
                // scroll-end on Chrome/Safari, and spacers scroll away with
                // the content so cards clip at the true screen edge).
                property("width", "calc(100% + 32px)")
                property("margin-left", "-16px")
                property("margin-right", "-16px")
                property("box-sizing", "border-box")
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("scrollbar-width", "none")
                property("-webkit-overflow-scrolling", "touch")
                property("scroll-snap-type", "x proximity")
                // The gutter is a spacer ELEMENT now (not container padding),
                // so snap must be told about it — without this the browser
                // snaps the first card flush to the edge on load, eating the
                // spacer.
                property("scroll-padding-left", "16px")
                property("gap", "12px")
            }
    ) {
        // Leading gutter — a spacer INSIDE the scroll (4px + the 12px flex
        // gap = the 16px page gutter at rest), so the first card slides all
        // the way to the screen edge instead of stopping at a fixed inset.
        Div(attrs = { style { property("flex", "0 0 4px") } })
        articles.forEach { article ->
            ArticleLink(article) {
            Box(
                modifier = Modifier
                    .mWidth(260)
                    .mHeight(170)
                    .mBorderRadius(20)
                    .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("scroll-snap-align", "start")
                        property("position", "relative")
                    }
            ) {
                Img(
                    src = article.coverOrFallback(),
                    alt = article.title,
                    attrs = Modifier
                        .fillMaxSize()
                        // Fill the whole card — no letterbox bands above/below.
                        .styleModifier {
                            property("object-fit", "cover"); property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                            property("display", "block")
                        }
                        .coverImgAttrs(article.sport)
                        .toAttrs()
                )
                // Bottom gradient + title overlay.
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .mPadding(12, 12)
                        .mGap(6)
                        .styleModifier {
                            property("position", "absolute")
                            property("left", "0")
                            property("right", "0")
                            property("bottom", "0")
                            property(
                                "background",
                                "linear-gradient(180deg, rgba(2,4,11,0) 0%, rgba(2,4,11,0.85) 70%)"
                            )
                        }
                ) {
                    SportChip(article.sport, mobile = true)
                    SpanText(
                        article.title,
                        modifier = Modifier
                            .mFontSize(16)
                            .fontWeight(600)
                            .mLineHeight(21)
                            .color(org.jetbrains.compose.web.css.Color("#FFFFFF")) // theme-invariant: over photo scrim
                            .then(lineClamp(2))
                    )
                }
            }
            }
        }
        // Trailing lead-out so the last card clears the edge.
        Div(attrs = { style { property("flex", "0 0 16px") } })
    }
}

@Composable
private fun NewsCardMobile(article: NewsArticleDto) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .mBorderRadius(20)
            .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
            .styleModifier {
                property("flex-shrink", "0")
                // Panel fill + outer-container gradient hairline.
                property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
            }
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Img(
                src = article.coverOrFallback(),
                alt = article.title,
                attrs = Modifier
                    .fillMaxWidth()
                    // Full width, natural height — the whole press photo shows,
                    // no crop, no letterbox bands.
                    .styleModifier {
                        property("height", "auto")
                        property("display", "block")
                    }
                    .coverImgAttrs(article.sport)
                    .toAttrs()
            )
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .mPadding(14, 14)
                    .mGap(8)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SportChip(article.sport, mobile = true)
                    SpanText(
                        shortDate(article.publishedAt),
                        modifier = Modifier.mFontSize(12).color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
                SpanText(
                    article.title,
                    modifier = Modifier
                        .mFontSize(18)
                        .fontWeight(600)
                        .mLineHeight(24)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                        .then(lineClamp(2))
                )
                if (article.summary.isNotBlank()) {
                    SpanText(
                        article.summary,
                        modifier = Modifier
                            .mFontSize(15)
                            .mLineHeight(21)
                            .color(com.arena_web.ui.theme.ThemeVars.Text2)
                            .then(lineClamp(3))
                    )
                }
            }
        }
    }
}

// =============================================================================
// Desktop
// =============================================================================

@Composable
fun NewsContentDesktop(
    modeToggle: @Composable () -> Unit = {},
    todayArticles: List<NewsArticleDto>,
    feedArticles: List<NewsArticleDto>,
    sort: NewsFeedSort,
    onSortChange: (NewsFeedSort) -> Unit,
    activeTag: String? = null,
    onClearTag: () -> Unit = {},
    onLoadMore: () -> Unit = {},
    showProBanner: Boolean = false,
    onProBannerClick: () -> Unit = {},
) {
    val t = LocalAppStrings.current
    // Same gate as mobile: 6 free cards, then the banner ends the feed —
    // infinite scroll is off while gated (nothing below to load into).
    InfiniteScrollTrigger(DESKTOP_SCROLL_ID, feedArticles.size, if (showProBanner) ({}) else onLoadMore)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .flexGrow(1)
            .minHeight(0.px)
            .styleModifier { property("align-items", "center") }
    ) {
        Column(
            modifier = Modifier
                .fillMaxHeight()
                .sWidth(1100)
                .styleModifier {
                    property("max-width", "100%")
                    property("padding-top", s(8))
                }
        ) {
            Div(
                attrs = {
                    id(DESKTOP_SCROLL_ID)
                    style {
                        property("width", "100%")
                        property("flex-grow", "1")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "none")
                    }
                }
            ) {
                Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(20)) }) {
                    // Top row: "Most discussed" opposite the news↔matches
                    // switcher (right-aligned, compact) — same pattern as the
                    // mobile top row. The toggle stays even when the curated
                    // strip is empty or a tag filter hides it.
                    val showToday = todayArticles.isNotEmpty() && activeTag == null
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (showToday) SectionHeader(t.newsTrendingTitle, mobile = false) else Box {}
                        modeToggle()
                    }
                    if (showToday) {
                        TodayCarouselDesktop(todayArticles)
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        if (activeTag != null) ActiveTagPill(activeTag, onClearTag, mobile = false)
                        else SectionHeader(t.newsAllTitle, mobile = false)
                        SortSegments(sort, onSortChange, mobile = false)
                    }

                    if (feedArticles.isEmpty()) {
                        Box(
                            modifier = Modifier.fillMaxWidth().minHeight(200.px),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                t.newsEmptyDay,
                                modifier = Modifier.sFontSize(17).color(com.arena_web.ui.theme.ThemeVars.Text2)
                            )
                        }
                    } else {
                        // Two-column grid of phone-style vertical cards — at the
                        // 720 column width each cell is ~350 wide, the same
                        // proportions as the mobile card. Raw Div: Kobweb Box
                        // stacks children even with display:grid overridden.
                        Div(
                            attrs = {
                                style {
                                    property("display", "grid")
                                    property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
                                    property("gap", s(18))
                                    property("width", "100%")
                                }
                            }
                        ) {
                            // Gated feed: 6 free cards (three full rows), then
                            // the banner spans both columns and ends the list.
                            val visible = if (showProBanner) feedArticles.take(6) else feedArticles
                            visible.forEach { article -> ArticleLink(article) { NewsCardDesktop(article) } }
                            if (showProBanner) {
                                Div(attrs = { style { property("grid-column", "1 / -1") } }) {
                                    NewsProBanner(mobile = false, onClick = onProBannerClick)
                                }
                            }
                        }
                    }
                    Div(attrs = { style { property("width", "100%"); property("flex", "0 0 ${s(40)}"); property("height", s(40)) } })
                }
            }
        }
    }
}

private const val TODAY_CAROUSEL_ID = "news-today-carousel"

@Composable
private fun TodayCarouselDesktop(articles: List<NewsArticleDto>) {
    // Mouse wheels only emit vertical deltas, so the horizontal strip reads
    // as "not scrolling" on desktop — redirect a dominant vertical wheel into
    // scrollLeft. Non-passive listener: preventDefault must stop the page
    // scroller from consuming the same gesture.
    DisposableEffect(Unit) {
        val el = kotlinx.browser.document.getElementById(TODAY_CAROUSEL_ID)
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val wheel = ev.unsafeCast<org.w3c.dom.events.WheelEvent>()
            val node = ev.currentTarget.unsafeCast<org.w3c.dom.Element>()
            if (kotlin.math.abs(wheel.deltaY) > kotlin.math.abs(wheel.deltaX) &&
                node.scrollWidth > node.clientWidth
            ) {
                node.scrollLeft += wheel.deltaY
                ev.preventDefault()
            }
        }
        el?.asDynamic()?.addEventListener("wheel", handler, js("({passive: false})"))
        onDispose { el?.asDynamic()?.removeEventListener("wheel", handler) }
    }
    Row(
        modifier = Modifier
            .id(TODAY_CAROUSEL_ID)
            .fillMaxWidth()
            .styleModifier {
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("scrollbar-width", "none")
                // No scroll-snap here — it fights the wheel redirect above
                // (re-snaps on every wheel step and the strip stutters).
                property("gap", s(14))
            }
    ) {
        articles.forEach { article ->
            ArticleLink(article) {
            Box(
                modifier = Modifier
                    .sWidth(356)
                    .sHeight(224)
                    .sBorderRadius(22)
                    .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
                    .cursor(Cursor.Pointer)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("scroll-snap-align", "start")
                        property("position", "relative")
                    }
            ) {
                Img(
                    src = article.coverOrFallback(),
                    alt = article.title,
                    attrs = Modifier
                        .fillMaxSize()
                        // Fill the whole card — no letterbox bands above/below.
                        .styleModifier {
                            property("object-fit", "cover"); property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                            property("display", "block")
                        }
                        .coverImgAttrs(article.sport)
                        .toAttrs()
                )
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPadding(14, 14)
                        .sGap(6)
                        .styleModifier {
                            property("position", "absolute")
                            property("left", "0")
                            property("right", "0")
                            property("bottom", "0")
                            property(
                                "background",
                                "linear-gradient(180deg, rgba(2,4,11,0) 0%, rgba(2,4,11,0.85) 70%)"
                            )
                        }
                ) {
                    SportChip(article.sport, mobile = false)
                    SpanText(
                        article.title,
                        modifier = Modifier
                            .sFontSize(20)
                            .fontWeight(600)
                            .sLineHeight(26)
                            .color(org.jetbrains.compose.web.css.Color("#FFFFFF")) // theme-invariant: over photo scrim
                            .then(lineClamp(2))
                    )
                }
            }
            }
        }
        // Trailing lead-out so the last card clears the clipped edge when the
        // strip is scrolled to the end (real spacer, not padding — see the
        // mobile carousel note).
        Div(attrs = { style { property("flex", "0 0 ${s(16)}") } })
    }
}

@Composable
private fun NewsCardDesktop(article: NewsArticleDto) {
    // Vertical card mirroring the phone layout (cover on top, copy below),
    // plus desktop-only hover: the card lifts and the cover gently zooms.
    var hovered by remember { mutableStateOf(false) }
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .sBorderRadius(20)
            .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .styleModifier {
                property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                property("transition", "transform 0.25s ease, box-shadow 0.25s ease")
                if (hovered) {
                    property("transform", "translateY(${s(-4)})")
                    property("box-shadow", "0 12px 28px rgba(0,0,0,0.45)")
                }
            }
    ) {
        // Clipping frame for the zooming cover — fixed height so rows of
        // cards keep a steady rhythm regardless of source image size.
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .sHeight(290)
                .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
                .styleModifier { property("flex-shrink", "0") }
        ) {
            Img(
                src = article.coverOrFallback(),
                alt = article.title,
                attrs = Modifier
                    .fillMaxSize()
                    // Fill the full card width; overflow crops from the bottom
                    // (top-anchored) so narrow covers don't letterbox.
                    .styleModifier {
                        property("object-fit", "cover"); property("object-position", "center top")
                        property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
                        property("display", "block")
                        property("transition", "transform 0.35s ease")
                        if (hovered) property("transform", "scale(1.05)")
                    }
                    .coverImgAttrs(article.sport)
                    .toAttrs()
            )
        }
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sPadding(18, 18)
                .sGap(10)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                SportChip(article.sport, mobile = false)
                SpanText(
                    shortDate(article.publishedAt),
                    modifier = Modifier.sFontSize(14).color(com.arena_web.ui.theme.ThemeVars.Text3)
                )
            }
            SpanText(
                article.title,
                modifier = Modifier
                    .sFontSize(22)
                    .fontWeight(600)
                    .sLineHeight(29)
                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    .then(lineClamp(2))
            )
            if (article.summary.isNotBlank()) {
                SpanText(
                    article.summary,
                    modifier = Modifier
                        .sFontSize(18)
                        .sLineHeight(25)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .then(lineClamp(3))
                )
            }
        }
    }
}

// =============================================================================
// Shared bits
// =============================================================================

@Composable
internal fun SectionHeader(text: String, mobile: Boolean) {
    SpanText(
        text,
        modifier = Modifier
            .then(if (mobile) Modifier.mFontSize(18) else Modifier.sFontSize(23))
            .fontWeight(600)
            .color(com.arena_web.ui.theme.ThemeVars.Text1)
    )
}

/**
 * Sort selector — Arena-style filter-pill dropdown ("Sort: New ⌄"). Same
 * trigger chrome and menu card as Arena's FilterPillDropdown; options: by
 * date, by popularity, favorites.
 */
@Composable
private fun SortSegments(sort: NewsFeedSort, onSortChange: (NewsFeedSort) -> Unit, mobile: Boolean) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var expanded by androidx.compose.runtime.remember { androidx.compose.runtime.mutableStateOf(false) }
    var hovered by androidx.compose.runtime.remember { androidx.compose.runtime.mutableStateOf(-1) }

    fun label(s: NewsFeedSort): String = when (s) {
        NewsFeedSort.NEW -> t.newsSortNew
        NewsFeedSort.POPULAR -> t.newsSortPopular
        NewsFeedSort.FAVORITES -> t.newsSortFavorites
    }

    // Outside-click close via document mousedown + stable DOM id — the
    // fixed-backdrop trick is unreliable inside transformed/scrolling
    // ancestors (same reasoning as Arena's FilterPillDropdown).
    val dropdownId = androidx.compose.runtime.remember {
        "news-sort-${kotlin.js.Date.now().toLong()}-${kotlin.random.Random.nextInt(0, 1_000_000)}"
    }
    androidx.compose.runtime.DisposableEffect(expanded) {
        if (!expanded) return@DisposableEffect onDispose {}
        val handler: (org.w3c.dom.events.Event) -> Unit = { ev ->
            val target = ev.target as? org.w3c.dom.Node
            val root = kotlinx.browser.document.getElementById(dropdownId)
            if (target != null && root != null && !root.contains(target)) expanded = false
        }
        kotlinx.browser.document.addEventListener("mousedown", handler)
        onDispose { kotlinx.browser.document.removeEventListener("mousedown", handler) }
    }

    Box(modifier = Modifier.id(dropdownId).position(org.jetbrains.compose.web.css.Position.Relative)) {
        // Pill trigger — "Sort: <value>".
        Box(
            modifier = Modifier
                .cursor(Cursor.Pointer)
                .onClick { expanded = !expanded }
                .then(if (mobile) Modifier.mBorderRadius(100).mPadding(8, 14) else Modifier.sBorderRadius(100).sPadding(10, 22))
                .styleModifier {
                    property("border", "0.3px solid transparent")
                    property(
                        "background",
                        "${AppGradients.InteractiveSecondary} padding-box, " +
                            "linear-gradient(0deg, var(--l-s2, #0B1628), var(--l-s2, #0B1628)) padding-box, " +
                            "${AppGradients.ButtonBorderGradientPrimary} border-box"
                    )
                },
            contentAlignment = Alignment.Center
        ) {
            Row(
                modifier = Modifier.then(if (mobile) Modifier.mGap(4) else Modifier.sGap(4)),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    "${t.newsSortLabel}: ",
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(12) else Modifier.sFontSize(15))
                        .fontWeight(500)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .styleModifier { property("white-space", "nowrap") }
                )
                SpanText(
                    label(sort),
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(12) else Modifier.sFontSize(15))
                        .fontWeight(500)
                        .color(sportColors.primary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }

        // Options menu — same card style as Arena's filter dropdown.
        if (expanded) {
            Column(
                modifier = Modifier
                    .position(org.jetbrains.compose.web.css.Position.Absolute)
                    .then(if (mobile) Modifier.mTop(44) else Modifier.sTop(52))
                    .styleModifier {
                        property("right", "0")
                        property("z-index", "100")
                        property("width", "max-content")
                        property("min-width", if (mobile) "160px" else s(200))
                        property("border-radius", if (mobile) "14px" else s(16))
                        property("padding", if (mobile) "6px" else s(8))
                        property("border", "0.3px solid transparent")
                        property(
                            "background",
                            "linear-gradient(6deg, rgba(0, 59, 174, 0.12) 26.9%, rgba(3, 87, 230, 0.12) 67.5%) padding-box, " +
                                "linear-gradient(0deg, var(--l-s3, #060E1E), var(--l-s3, #060E1E)) padding-box, " +
                                "${AppGradients.ButtonBorderGradientPrimary} border-box"
                        )
                        property("box-shadow", "0 8px 24px rgba(0,0,0,0.4)")
                    }
            ) {
                NewsFeedSort.entries.forEachIndexed { index, option ->
                    val isSelected = option == sort
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .cursor(Cursor.Pointer)
                            .then(if (mobile) Modifier.mBorderRadius(10).mPadding(10, 12) else Modifier.sBorderRadius(10).sPadding(10, 14))
                            .onMouseEnter { hovered = index }
                            .onMouseLeave { hovered = -1 }
                            .onClick {
                                onSortChange(option)
                                expanded = false
                            }
                            .styleModifier {
                                if (hovered == index) property("background", "rgba(60, 121, 241, 0.12)")
                            },
                        contentAlignment = Alignment.CenterStart
                    ) {
                        SpanText(
                            label(option),
                            modifier = Modifier
                                .then(if (mobile) Modifier.mFontSize(13) else Modifier.sFontSize(15))
                                .fontWeight(500)
                                .color(if (isSelected) org.jetbrains.compose.web.css.Color(sportColors.primary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                                .styleModifier { property("white-space", "nowrap") }
                        )
                    }
                }
            }
        }
    }
}

/** Display label for a sport key — nice-cases the non-core "Other" sports
 *  (mma → MMA, formula1 → Formula 1); core sports just capitalize. */
internal fun sportLabel(sport: String): String = when (sport.lowercase()) {
    "mma" -> "MMA"
    "formula1" -> "Formula 1"
    else -> sport.replaceFirstChar { it.uppercase() }
}

@Composable
internal fun SportChip(sport: String, mobile: Boolean) {
    val colors = SportColors.forSport(sport)
    Box(
        modifier = Modifier
            .then(if (mobile) Modifier.mBorderRadius(100).mPadding(4, 10) else Modifier.sBorderRadius(100).sPadding(4, 12))
            .backgroundColor(colors.primary)
            .styleModifier { property("width", "fit-content") },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            sportLabel(sport),
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(11) else Modifier.sFontSize(12))
                .fontWeight(500)
                .color(colors.onPrimary)
        )
    }
}

internal fun NewsArticleDto.coverOrFallback(): String =
    coverImage.ifBlank { AppIcons.statisticBackground(sport) }

/** Card-cover loading behaviour: fetch lazily as the card nears the viewport
 *  (infinite scroll must not burst-download every appended page's covers),
 *  decode off the main thread, and swap rotted hotlinks for the sport art. */
internal fun Modifier.coverImgAttrs(sport: String): Modifier =
    attrsModifier {
        attr("loading", "lazy")
        attr("decoding", "async")
        attr("onerror", "this.onerror=null;this.src='${AppIcons.statisticBackground(sport)}'")
    }

/** Active hashtag filter pill — "#tag ×", tap clears the filter. */
@Composable
private fun ActiveTagPill(tag: String, onClear: () -> Unit, mobile: Boolean) {
    val sportColors = LocalSportColors.current
    Row(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClear() }
            .then(if (mobile) Modifier.mBorderRadius(100).mPadding(6, 14).mGap(8) else Modifier.sBorderRadius(100).sPadding(8, 16).sGap(8))
            .styleModifier {
                property("border", "1px solid ${sportColors.primary}")
                property("background", "${sportColors.primary}1F")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            "#$tag",
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(14) else Modifier.sFontSize(15))
                .fontWeight(600)
                .styleModifier { property("color", sportColors.primary.toString()) }
        )
        SpanText(
            "×",
            modifier = Modifier
                .then(if (mobile) Modifier.mFontSize(16) else Modifier.sFontSize(17))
                .color(com.arena_web.ui.theme.ThemeVars.Text2)
        )
    }
}

/**
 * The PRO gate closing the news feed for guests and signed-in
 * non-subscribers — the free cards above it are all they get, so the copy
 * sells the rest of the feed. The click goes through the caller: guests get
 * the sign-in gate first, everyone else the news-variant paywall.
 * Centered composition over the night-stadium hero (dark edges carry the
 * white copy without extra chrome).
 */
@Composable
private fun NewsProBanner(mobile: Boolean, onClick: () -> Unit) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (mobile) Modifier.mBorderRadius(20) else Modifier.sBorderRadius(20))
            .overflow(com.varabyte.kobweb.compose.css.Overflow.Hidden)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("position", "relative")
                property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                property("background", "var(--arena-bg)")
            }
    ) {
        Img(
            src = "/images/home/hero-stadium.png",
            alt = "",
            attrs = {
                attr("loading", "lazy")
                attr("decoding", "async")
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property("width", "100%")
                    property("height", "100%")
                    property("object-fit", "cover")
                    property("object-position", "center 40%")
                    property("display", "block")
                }
            }
        )
        // Soft vignette: keeps the lit pitch glowing through the middle while
        // guaranteeing contrast for the copy at the center.
        Div(
            attrs = {
                style {
                    property("position", "absolute")
                    property("inset", "0")
                    property(
                        "background",
                        "radial-gradient(80% 90% at 50% 50%, rgba(2,4,11,0.45) 0%, rgba(2,4,11,0.25) 55%, rgba(2,4,11,0.7) 100%)"
                    )
                }
            }
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .then(if (mobile) Modifier.mPadding(28, 20).mGap(10) else Modifier.sPadding(44, 32).sGap(12))
                .styleModifier {
                    property("position", "relative")
                    property("box-sizing", "border-box")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("text-align", "center")
                    // Tall enough for the floodlit pitch to actually show
                    // around the copy — content alone would collapse the
                    // stadium into a thin dark strip.
                    property("min-height", if (mobile) m(220) else s(240))
                }
        ) {
            SpanText(
                t.newsProBannerTitle,
                modifier = Modifier
                    .then(if (mobile) Modifier.mFontSize(20).mLineHeight(26) else Modifier.sFontSize(26).sLineHeight(32))
                    .fontWeight(700)
                    .color(org.jetbrains.compose.web.css.Color("#FFFFFF")) // theme-invariant: over stadium photo
                    .styleModifier { property("text-shadow", "0 2px 14px rgba(0,0,0,0.7)") }
            )
            SpanText(
                t.newsProBannerSubtitle,
                modifier = Modifier
                    .then(if (mobile) Modifier.mFontSize(13).mLineHeight(19) else Modifier.sFontSize(15).sLineHeight(22))
                    .color(org.jetbrains.compose.web.css.Color("#E4ECF8")) // theme-invariant: over stadium photo
                    .styleModifier {
                        property("max-width", if (mobile) "100%" else "520px")
                        property("text-shadow", "0 1px 10px rgba(0,0,0,0.7)")
                    }
            )
            Box(
                modifier = Modifier
                    .then(if (mobile) Modifier.mBorderRadius(100).mPadding(12, 26) else Modifier.sBorderRadius(100).sPadding(13, 32).sMarginTop(8))
                    .styleModifier {
                        if (mobile) property("margin-top", m(6))
                        property("width", "fit-content")
                        property(
                            "background",
                            "linear-gradient(180deg, ${sportColors.primary} 0%, ${sportColors.secondary} 100%)"
                        )
                        property("border", "0.3px solid rgba(255, 255, 255, 0.3)")
                        property(
                            "box-shadow",
                            "0 10px 26px color-mix(in srgb, ${sportColors.primary} 45%, transparent)"
                        )
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    t.newsProBannerCta,
                    modifier = Modifier
                        .then(if (mobile) Modifier.mFontSize(15) else Modifier.sFontSize(16))
                        .fontWeight(600)
                        .color(sportColors.onPrimary)
                )
            }
        }
    }
}

/** Crawlable article link wrapper — real <a href> + SPA navigation. */
@Composable
internal fun ArticleLink(article: NewsArticleDto, content: @Composable () -> Unit) {
    val ctx = rememberPageContext()
    NavAnchor("/news/${article.id}", onNavigate = { ctx.router.navigateTo("/news/${article.id}") }) {
        content()
    }
}

/** dd.MM label from an ISO timestamp (or empty when unpublished). */
internal fun shortDate(publishedAt: String?): String {
    val date = publishedAt?.take(10) ?: return ""
    val parts = date.split("-")
    return if (parts.size == 3) "${parts[2]}.${parts[1]}" else date
}

internal fun lineClamp(lines: Int): Modifier = Modifier.styleModifier {
    property("display", "-webkit-box")
    property("-webkit-line-clamp", lines.toString())
    property("-webkit-box-orient", "vertical")
    property("overflow", "hidden")
}
